/**
 * Per-thread context settings: visibility (private/public, set from
 * Collaborate) and the composer strip's sticky jurisdiction selection that
 * scopes eve's knowledge-base retrieval.
 *
 * Same shape as `state/collaboration.ts`: a per-thread cached atom over
 * `/api/amoreg/thread-settings`, mutations POST then refresh. Everything
 * flows through Effect atoms.
 *
 * @module state/threadSettings
 */
import { useAtomValue } from "@effect/atom-react";
import * as Cause from "effect/Cause";
import type * as Duration from "effect/Duration";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
import * as Schedule from "effect/Schedule";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useMemo } from "react";

import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { appAtomRegistry } from "../rpc/atomRegistry";

const OrgJurisdiction = Schema.Struct({
  slug: Schema.String,
  name: Schema.String,
  avatarUrl: Schema.NullOr(Schema.String),
});
export type OrgJurisdiction = typeof OrgJurisdiction.Type;

const SettingsSchema = Schema.Struct({
  threadId: Schema.String,
  organizationId: Schema.String,
  visibility: Schema.Literals(["private", "public"]),
  jurisdictionSlugs: Schema.NullOr(Schema.Array(Schema.String)),
  orgJurisdictions: Schema.Array(OrgJurisdiction),
  canSetVisibility: Schema.Boolean,
  canSetJurisdictions: Schema.Boolean,
});
type SettingsSnapshot = typeof SettingsSchema.Type;

/** No settings feature (plain T3 dev) or the thread isn't a server thread yet. */
const UNAVAILABLE: SettingsSnapshot = {
  threadId: "",
  organizationId: "",
  visibility: "private",
  jurisdictionSlugs: null,
  orgJurisdictions: [],
  canSetVisibility: false,
  canSetJurisdictions: false,
};

const decodeSettings = Schema.decodeUnknownEffect(SettingsSchema);

export class ThreadSettingsRequestError extends Schema.TaggedErrorClass<ThreadSettingsRequestError>()(
  "ThreadSettingsRequestError",
  { status: Schema.Number },
) {
  /** 5xx and the odd 429 are worth another go; 4xx is an answer. */
  get transient(): boolean {
    return this.status >= 500 || this.status === 429;
  }
}

/** One settings request gets this long before the strip stops waiting on it. */
export const SETTINGS_REQUEST_TIMEOUT: Duration.Input = "8 seconds";

const isTransientSettingsFailure = (error: unknown): boolean =>
  error instanceof ThreadSettingsRequestError ? error.transient : true;

/**
 * The transport policy every settings request runs under: each attempt is
 * bounded by `timeout` (the fetch is aborted when it fires), and transient
 * failures — timeouts, network drops, 5xx — are retried on a jittered
 * exponential backoff. A 4xx is a decision, not a hiccup, and surfaces at
 * once. The caller never sees a hung request: the effect settles with a
 * value, a `ThreadSettingsRequestError`, or a `TimeoutError`.
 */
export const settingsTransport =
  (options?: {
    readonly timeout?: Duration.Input;
    readonly backoff?: Schedule.Schedule<unknown>;
    readonly retries?: number;
  }) =>
  <A, E, R>(self: Effect.Effect<A, E, R>) =>
    self.pipe(
      Effect.timeout(options?.timeout ?? SETTINGS_REQUEST_TIMEOUT),
      Effect.retry({
        times: options?.retries ?? 2,
        schedule: options?.backoff ?? Schedule.exponential("300 millis").pipe(Schedule.jittered),
        while: isTransientSettingsFailure,
      }),
    );

/**
 * What the composer strip (and the banner above it) should say about a
 * settings atom: nothing yet, settled, or failed — and why. Derived from the
 * atom's `AsyncResult` so both surfaces read one truth.
 */
export type ThreadSettingsLoadState =
  | { readonly _tag: "loading" }
  | { readonly _tag: "ready" }
  | { readonly _tag: "failed"; readonly reason: "timeout" | "request"; readonly status: number | null };

const LOADING: ThreadSettingsLoadState = { _tag: "loading" };
const READY: ThreadSettingsLoadState = { _tag: "ready" };

export function describeSettingsLoad<A>(
  result: AsyncResult.AsyncResult<A, unknown>,
): ThreadSettingsLoadState {
  if (AsyncResult.isFailure(result)) {
    const error = Option.getOrNull(AsyncResult.error(result));
    if (Cause.isTimeoutError(error)) {
      return { _tag: "failed", reason: "timeout", status: null };
    }
    return {
      _tag: "failed",
      reason: "request",
      status: error instanceof ThreadSettingsRequestError ? error.status : null,
    };
  }
  return Option.isSome(AsyncResult.value(result)) ? READY : LOADING;
}

/**
 * Settings chosen on a draft, before its server thread exists. Keyed by the
 * draft's pre-allocated thread id (drafts and their promoted threads share
 * the id), and flushed to the server by `fetchSettings` the first time the
 * thread resolves — so a jurisdiction scope picked mid-draft sticks to the
 * thread the first message creates.
 */
export interface PendingDraftSettings {
  jurisdictionSlugs?: ReadonlyArray<string> | null;
}
const pendingDraftSettings = new Map<string, PendingDraftSettings>();

export const readPendingDraftSettings = (threadId: string): PendingDraftSettings | undefined =>
  pendingDraftSettings.get(threadId);

export function setPendingDraftSettings(
  threadId: string,
  patch: PendingDraftSettings,
  organizationId: string,
): void {
  const current = pendingDraftSettings.get(threadId) ?? {};
  pendingDraftSettings.set(threadId, { ...current, ...patch });
  // The draft strip reads through the org-defaults atom; refresh re-renders
  // it with the pending overlay applied.
  appAtomRegistry.refresh(orgDefaultsAtom(organizationId));
}

const postSettings = (threadId: string, patch: PendingDraftSettings) =>
  fetch(resolvePrimaryEnvironmentHttpUrl("/api/amoreg/thread-settings", { threadId }), {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(patch),
  });

const fetchSettings = (threadId: string) =>
  Effect.gen(function* () {
    if (threadId.length === 0) return UNAVAILABLE;
    const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/thread-settings", { threadId });
    const response = yield* Effect.tryPromise((signal) =>
      fetch(url, { credentials: "include", signal }),
    );
    if (response.status === 404) return UNAVAILABLE;
    if (!response.ok) {
      return yield* Effect.fail(new ThreadSettingsRequestError({ status: response.status }));
    }
    const json = yield* Effect.tryPromise(() => response.json());
    const settings = yield* decodeSettings(json);
    // The thread exists now: flush anything chosen while it was a draft, then
    // read back the applied state.
    const pending = pendingDraftSettings.get(threadId);
    if (pending && settings.organizationId.length > 0) {
      pendingDraftSettings.delete(threadId);
      yield* Effect.tryPromise(() => postSettings(threadId, pending)).pipe(
        Effect.orElseSucceed(() => null),
      );
      const reread = yield* Effect.tryPromise(() =>
        fetch(url, { credentials: "include" }).then((r) => (r.ok ? r.json() : null)),
      ).pipe(Effect.orElseSucceed(() => null));
      if (reread) {
        return yield* decodeSettings(reread);
      }
    }
    return settings;
  }).pipe(settingsTransport());

const settingsRuntime = Atom.runtime(Layer.empty);
const settingsAtom = Atom.family((threadId: string) =>
  settingsRuntime
    .atom(fetchSettings(threadId))
    .pipe(Atom.withLabel(`web-amoreg:thread-settings:${threadId}`)),
);

export interface ThreadSettingsView {
  /** False until the thread resolves to a tenant workspace. */
  readonly available: boolean;
  readonly visibility: "private" | "public";
  /** Null means all of the org's jurisdictions (the default). */
  readonly jurisdictionSlugs: ReadonlySet<string> | null;
  readonly orgJurisdictions: readonly OrgJurisdiction[];
  readonly canSetVisibility: boolean;
  readonly canSetJurisdictions: boolean;
  readonly isPending: boolean;
  /** Loading, settled, or failed (and why) — drives skeletons and the banner. */
  readonly load: ThreadSettingsLoadState;
  readonly refresh: () => void;
}

export function useThreadSettings(threadId: string): ThreadSettingsView {
  const atom = useMemo(() => settingsAtom(threadId), [threadId]);
  const result = useAtomValue(atom);

  const refresh = useCallback(() => {
    appAtomRegistry.refresh(atom);
  }, [atom]);

  return useMemo(() => {
    const snapshot = Option.getOrNull(AsyncResult.value(result));
    return {
      available: snapshot !== null && snapshot.organizationId.length > 0,
      visibility: snapshot?.visibility ?? "private",
      jurisdictionSlugs:
        snapshot?.jurisdictionSlugs == null ? null : new Set(snapshot.jurisdictionSlugs),
      orgJurisdictions: snapshot?.orgJurisdictions ?? [],
      canSetVisibility: snapshot?.canSetVisibility ?? false,
      canSetJurisdictions: snapshot?.canSetJurisdictions ?? false,
      isPending: result.waiting && snapshot === null,
      load: describeSettingsLoad(result),
      refresh,
    };
  }, [result, refresh]);
}

const orgDefaultsAtom = Atom.family((organizationId: string) =>
  settingsRuntime
    .atom(
      Effect.gen(function* () {
        if (organizationId.length === 0) return UNAVAILABLE;
        const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/thread-settings", {
          organizationId,
        });
        const response = yield* Effect.tryPromise((signal) =>
          fetch(url, { credentials: "include", signal }),
        );
        if (response.status === 404) return UNAVAILABLE;
        if (!response.ok) {
          return yield* Effect.fail(new ThreadSettingsRequestError({ status: response.status }));
        }
        const json = yield* Effect.tryPromise(() => response.json());
        return yield* decodeSettings(json);
      }).pipe(settingsTransport()),
    )
    .pipe(Atom.withLabel(`web-amoreg:org-default-settings:${organizationId}`)),
);

/**
 * The org's jurisdictions with read-only default settings — the composer
 * strip on a draft, before the server thread exists to carry real settings.
 */
export function useOrgDefaultSettings(organizationId: string): ThreadSettingsView {
  const atom = useMemo(() => orgDefaultsAtom(organizationId), [organizationId]);
  const result = useAtomValue(atom);

  const refresh = useCallback(() => {
    appAtomRegistry.refresh(atom);
  }, [atom]);

  return useMemo(() => {
    const snapshot = Option.getOrNull(AsyncResult.value(result));
    return {
      available: snapshot !== null && snapshot.organizationId.length > 0,
      visibility: snapshot?.visibility ?? "private",
      jurisdictionSlugs: null,
      orgJurisdictions: snapshot?.orgJurisdictions ?? [],
      canSetVisibility: false,
      canSetJurisdictions: false,
      isPending: result.waiting && snapshot === null,
      load: describeSettingsLoad(result),
      refresh,
    };
  }, [result, refresh]);
}

export type ComposerContextSource = "thread" | "org" | "none";

export interface ComposerContextSettings {
  /**
   * Where the strip's settings come from: the thread's own once its server
   * thread exists, the active org's defaults while it is still a draft, or
   * nothing (no tenant context — the local git/env strip takes over).
   */
  readonly source: ComposerContextSource;
  readonly settings: ThreadSettingsView;
  readonly load: ThreadSettingsLoadState;
  readonly retry: () => void;
}

/**
 * The settings source behind the composer strip for one thread. The strip
 * renders from `settings`; the banner above the composer renders from
 * `load`. One hook so "loading" and "failed" mean the same thing to both.
 */
export function useComposerContextSettings(
  threadId: string,
  organizationId: string | null,
): ComposerContextSettings {
  const thread = useThreadSettings(threadId);
  // Drafts have no server thread yet: the org's defaults stand in. Both
  // lookups run in parallel (the org atom is shared across the org's drafts)
  // so a fresh draft pays one round-trip, not two.
  const org = useOrgDefaultSettings(thread.available ? "" : (organizationId ?? ""));
  // One outage fails both atoms; one retry must revive both, or the strip
  // stays on whichever failure the other lookup hid.
  const retry = useCallback(() => {
    thread.refresh();
    org.refresh();
  }, [thread.refresh, org.refresh]);
  return useMemo(() => {
    if (thread.available) {
      return { source: "thread", settings: thread, load: thread.load, retry };
    }
    if (thread.load._tag !== "ready") {
      // Still finding out whether the thread carries its own settings.
      return { source: "none", settings: thread, load: thread.load, retry };
    }
    if (organizationId === null) {
      return { source: "none", settings: thread, load: READY, retry };
    }
    return {
      source: org.available ? "org" : "none",
      settings: org,
      load: org.load,
      retry,
    };
  }, [org, organizationId, retry, thread]);
}

/** POST a settings change, then refresh the thread's atom. */
export async function mutateThreadSettings(
  threadId: string,
  patch: {
    readonly visibility?: "private" | "public";
    readonly jurisdictionSlugs?: ReadonlyArray<string> | null;
  },
): Promise<boolean> {
  const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/thread-settings", { threadId });
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(patch),
  });
  appAtomRegistry.refresh(settingsAtom(threadId));
  return response.ok;
}
